import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Star } from "lucide-react";
import LeadForm from "../components/LeadForm";
import PageHero from "../components/PageHero";

const HERO_IMG = "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1800&q=80";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function CapacitacionesPage() {
  const { t } = useTranslation();
  const programs = t("capacitaciones.programs.items", { returnObjects: true });
  const barriers = t("capacitaciones.barriers.items", { returnObjects: true });
  const comparison = t("capacitaciones.comparison", { returnObjects: true });

  return (
    <main>
      <PageHero
        eyebrow={t("capacitaciones.hero.eyebrow")}
        title={t("capacitaciones.hero.title")}
        subtitle={t("capacitaciones.hero.subtitle")}
        image={HERO_IMG}
        imageAlt="Equipo en sesión de capacitación corporativa"
      />

      {/* Programs */}
      <section className="section-y">
        <div className="container-page">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-bold mb-12 max-w-2xl">
            {t("capacitaciones.programs.title")}
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-5">
            {programs.map((p, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className={`glass-card p-8 relative ${p.featured ? "ring-2 ring-[#FF8C00]" : ""}`}>
                {p.featured && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-[#FF8C00] text-white">
                    <Star className="w-3 h-3" /> Recomendado
                  </span>
                )}
                <p className="eyebrow mb-2">{p.duration}</p>
                <h3 className="text-2xl font-semibold mb-2">{p.name}</h3>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">{p.audience}</p>
                <p className="text-muted-foreground leading-relaxed mb-6">{p.description}</p>
                <ul className="space-y-2">
                  {p.outcomes.map((o, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Barriers */}
      <section className="section-y bg-secondary/30 border-y border-border">
        <div className="container-page">
          <motion.div {...fadeUp} className="max-w-2xl mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t("capacitaciones.barriers.title")}</h2>
            <p className="text-muted-foreground text-lg">{t("capacitaciones.barriers.subtitle")}</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {barriers.map((b, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }}
                className="p-6 rounded-2xl border border-border bg-card/40 relative">
                <div className="text-3xl font-bold text-primary/30 mb-3">0{i + 1}</div>
                <h3 className="font-semibold mb-2">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="section-y">
        <div className="container-page">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-bold mb-10">{comparison.title}</motion.h2>
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full">
              <thead>
                <tr className="bg-secondary/50">
                  {comparison.headers.map((h, i) => (
                    <th key={i} className={`text-left p-5 text-sm font-semibold ${i === 2 ? "text-[#FF8C00]" : ""}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparison.rows.map((row, i) => (
                  <tr key={i} className="border-t border-border">
                    {row.map((cell, j) => (
                      <td key={j} className={`p-5 text-sm ${j === 0 ? "font-medium" : "text-muted-foreground"} ${j === 2 ? "bg-[#FF8C00]/5" : ""}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="section-y bg-secondary/30 border-t border-border">
        <div className="container-page max-w-3xl">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-bold mb-3 text-center">
            Hablemos de tu programa
          </motion.h2>
          <p className="text-center text-muted-foreground mb-10">
            Cuéntanos sobre tu equipo y diseñaremos una propuesta a medida.
          </p>
          <div className="glass-card p-8 md:p-10">
            <LeadForm pageOrigin="capacitaciones" defaultService="capacitaciones" />
          </div>
        </div>
      </section>
    </main>
  );
}
